/**
 * MapEmbed — drop-in replacement for GoogleMapsEmbed.
 * Shows a small MapLibre map centered on an address (geocoded via API proxy).
 */
import React, { useEffect, useState } from 'react';
import GoogleMap, { Marker } from './GoogleMap';
import api from '../../api/client';

export default function MapEmbed({ address, height = 250, className = '', streetView = false, zoom = 15, coords: known = null }) {
  // Known coordinates (e.g. the FieldRoutes lat/lng on the customer) skip the geocode call.
  const knownLat = Number(known?.lat);
  const knownLng = Number(known?.lng);
  const hasKnown = Number.isFinite(knownLat) && Number.isFinite(knownLng) && (knownLat !== 0 || knownLng !== 0);
  const [coords, setCoords] = useState(hasKnown ? { lat: knownLat, lng: knownLng } : null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (hasKnown) { setCoords({ lat: knownLat, lng: knownLng }); return; }
    if (!address) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/maps/geocode/', { params: { address } });
        if (cancelled) return;
        if (data?.result?.lat && data?.result?.lng) {
          setCoords({ lat: data.result.lat, lng: data.result.lng });
        } else {
          setFailed(true);
        }
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => { cancelled = true; };
  }, [address, hasKnown, knownLat, knownLng]);

  if (!address && !hasKnown) return null;

  if (!coords) {
    return (
      <div className={className} style={{
        height, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'var(--bg-panel, #f1f5f9)', borderRadius: 12,
        border: '1px solid var(--border, #e2e8f0)', color: 'var(--text-sub, #64748b)',
        fontSize: 13,
      }}>
        {failed ? "Couldn't place this address on the map." : 'Loading map...'}
      </div>
    );
  }

  return (
    <div className={className} style={{ height, borderRadius: 12, overflow: 'hidden' }}>
      <GoogleMap
        latitude={coords.lat}
        longitude={coords.lng}
        zoom={zoom}
        streetViewControl={streetView}
        style={{ minHeight: height }}
      >
        <Marker latitude={coords.lat} longitude={coords.lng}>
          <div style={{
            width: 24, height: 24, borderRadius: '50%',
            background: 'var(--accent)', border: '3px solid var(--bg-1)',
            boxShadow: 'var(--shadow-md, none)',
          }} />
        </Marker>
      </GoogleMap>
    </div>
  );
}
